/**
 * Mobile Application tab.
 *
 * Parity target: Angular `mobileappTemplate` (config.component.html
 * lines 1668–1740). Covers iOS/Android versions surfaced to the mobile
 * app and the platform-wide maintenance banner content.
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import {
  buildMobileAppPayload,
  saveConfig,
} from '../../services/configService'
import type { MaintenanceFlag, MobileAppFormValues } from '../../types/config'
import {
  FieldLegend,
  LabeledInput,
  LabeledTextArea,
  SegmentedButtonGroup,
  } from './formPrimitives'
import { SectionCard } from '@/components/ui/SectionCard'

import type { MobileAppTabProps } from '../../types'

const EMPTY: MobileAppFormValues = {
  ANDROID_VERSION: '',
  IOS_VERSION: '',
  APPLICATION_MAINTENANCE: '',
  MAINTENANCE_CONTENT: '',
}

export function MobileAppTab({ initialValues, isLoading, onSaved }: MobileAppTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<MobileAppFormValues>({ defaultValues: EMPTY, mode: 'onChange' })

  useEffect(() => {
    if (initialValues) reset(initialValues)
  }, [initialValues, reset])

  const onSubmit = async (values: MobileAppFormValues) => {
    try {
      setSubmitLoading(true)
      await saveConfig(buildMobileAppPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <SectionCard number={1} title="Application Version">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          <LabeledInput label="Android Version" {...register('ANDROID_VERSION')} />
          <LabeledInput label="iPhone Version" {...register('IOS_VERSION')} />
        </div>
      </SectionCard>

      <SectionCard number={2} title="Mobile Application">
        <FieldLegend label="Application down for maintenance">
          <SegmentedButtonGroup
            ariaLabel="Application maintenance mode"
            value={watch('APPLICATION_MAINTENANCE') ?? ''}
            onChange={(v) =>
              setValue('APPLICATION_MAINTENANCE', v as MaintenanceFlag, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            options={[
              { value: '1', label: 'YES' },
              { value: '0', label: 'NO' },
            ]}
          />
        </FieldLegend>
        <div className="mt-4 max-w-2xl">
          <LabeledTextArea
            label="Maintenance Content"
            rows={5}
            {...register('MAINTENANCE_CONTENT')}
          />
        </div>
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
  )
}
